/* =========================================================
   Seed dataset — the 10 real organizations extracted from
   EXAMPLES.xlsx (TEDI / Tecnología y Educación Innovadora
   E.I.R.L — ORG10203 included), plus the derived registries
   the prototype seeds on top of them.
   ========================================================= */
import type {
  AppUser, Assignment, AuthPolicy, ComplianceDoc, DemoUser,
  Instructor, LegalRequest, ManagedRole, Notification, Organization, Site,
} from '@/types';
import { mulberry32 } from '@/lib/rng';
import { buildSamplePdf, toBase64 } from '@/lib/samplePdf';
import { keysWithPrefix, readJSON } from '@/lib/storage';
import { FNAMES, LNAMES } from './catalogs';
import raw from './seed.json';

interface RawSeed {
  orgs: Organization[];
  sites: Site[];
  instructors: Instructor[];
  assignments: Assignment[];
}

const SEED = raw as unknown as RawSeed;

/** Deep copies — the mock source mutates these in memory. */
export function loadEntities() {
  return {
    orgs: SEED.orgs.map((o) => ({ ...o })),
    sites: SEED.sites.map((s) => ({ ...s })),
    instructors: SEED.instructors.map((i) => ({ ...i })),
    assignments: SEED.assignments.map((a) => ({ ...a })),
  };
}

/* ---------------- Compliance registry ---------------- */

const GEN_BASE = new Date('2026-06-09T00:00:00');

const COMP_TITLES = [
  'Reporte de incidencias pendientes',
  'Código de conducta del socio',
  'Buenas prácticas de seguridad',
  'Política de cumplimiento normativo',
  'Manual de buenas prácticas ATC',
  'Lineamientos de uso de marca',
];

const compSlug = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

export type ComplianceRegistry = Record<string, ComplianceDoc[]>;

/**
 * Roughly half the organizations arrive with 1–3 published documents at
 * assorted verification levels, so the Compliance column shows real data
 * from the first render. Uses its own RNG stream so it cannot disturb the
 * other seeds.
 */
export function buildComplianceRegistry(orgs: Organization[]): ComplianceRegistry {
  const RC = mulberry32(771202);
  const recentDate = (maxDays: number) => {
    const t = new Date(GEN_BASE);
    t.setDate(t.getDate() - Math.floor(RC() * maxDays));
    return t.toISOString().slice(0, 10);
  };

  const reg: ComplianceRegistry = {};
  orgs.forEach((o) => {
    if (RC() > 0.55) return;
    const nDocs = 1 + Math.floor(RC() * 3);
    const docs: ComplianceDoc[] = [];
    const startT = Math.floor(RC() * COMP_TITLES.length);

    for (let k = 0; k < nDocs; k++) {
      const title = COMP_TITLES[(startT + k) % COMP_TITLES.length];
      const nPages = 3 + Math.floor(RC() * 4);
      const pdf = buildSamplePdf(o.OrgName, o.OrgId, nPages, title);
      const readN = Math.floor(RC() * (nPages + 1));
      const pubDate = recentDate(150);
      const pct = Math.round((readN / nPages) * 100);

      // every completed (100%) verification is logged with its exact
      // finish timestamp; some documents were re-verified more than once.
      let history: string[] = [];
      let verifiedAt: string | null = null;
      let verifiedDate: string | null = null;
      if (pct === 100) {
        const nVerif = RC() < 0.4 ? 2 + Math.floor(RC() * 2) : 1;
        let off = 1 + Math.floor(RC() * 6);
        for (let h = 0; h < nVerif; h++) {
          const v = new Date(pubDate);
          v.setDate(v.getDate() + off);
          v.setHours(8 + Math.floor(RC() * 11), Math.floor(RC() * 60), Math.floor(RC() * 60), 0);
          history.push((v > GEN_BASE ? new Date(GEN_BASE.getTime() - h * 3600000) : v).toISOString());
          off += 2 + Math.floor(RC() * 12);
        }
        history = Array.from(new Set(history)).sort();
        verifiedAt = history[history.length - 1];
        verifiedDate = verifiedAt.slice(0, 10);
      }

      docs.push({
        id: `CMP-${o.OrgId}-${k + 1}`,
        title,
        name: `${compSlug(title)}_${o.OrgId.toLowerCase().replace(/-/g, '_')}.pdf`,
        size: pdf.length,
        date: pubDate,
        kind: 'pdf',
        dataUrl: 'data:application/pdf;base64,' + toBase64(pdf),
        total: nPages,
        pages: Array.from({ length: readN }, (_, i) => i + 1),
        pct,
        verifiedDate,
        verifiedAt,
        history,
      });
    }
    docs.sort((a, b) => b.date.localeCompare(a.date));
    reg[o.OrgId] = docs;
  });

  hydrateCompliance(reg);
  return reg;
}

interface StoredProgress {
  pct?: number;
  pages?: number[];
  total?: number;
  verifiedDate?: string | null;
  verifiedAt?: string | null;
  history?: string[];
}

/** User uploads and verification progress win over the seeds. */
function hydrateCompliance(reg: ComplianceRegistry) {
  // documents uploaded by the user
  keysWithPrefix('etc_comp_user_').forEach(({ key, suffix: orgId }) => {
    const arr = readJSON<Partial<ComplianceDoc>[]>(key, []);
    if (!Array.isArray(arr)) return;
    const docs = reg[orgId] || (reg[orgId] = []);
    arr.forEach((m) => {
      if (!m || !m.id || !m.name || docs.some((d) => d.id === m.id)) return;
      docs.unshift({
        title: m.title ?? m.name.replace(/\.(pdf|docx)$/i, ''),
        size: m.size ?? 0,
        date: m.date ?? '',
        dataUrl: m.dataUrl ?? '',
        history: m.history ?? [],
        total: m.total ?? 0,
        pages: m.pages ?? [],
        pct: m.pct ?? 0,
        verifiedDate: m.verifiedDate ?? null,
        verifiedAt: m.verifiedAt ?? null,
        id: m.id,
        name: m.name,
        user: true,
        kind: /\.docx$/i.test(m.name) ? 'docx' : 'pdf',
      });
    });
  });

  // legacy single-document uploads, migrated read-only
  keysWithPrefix('etc_comp_doc_').forEach(({ key, suffix: orgId }) => {
    const meta = readJSON<Partial<ComplianceDoc> | null>(key, null);
    if (!meta || !meta.name) return;
    const docs = reg[orgId] || (reg[orgId] = []);
    const legacyId = 'CMP-LEGACY-' + orgId;
    if (docs.some((d) => d.id === legacyId)) return;
    docs.unshift({
      id: legacyId,
      title: meta.name.replace(/\.(pdf|docx)$/i, ''),
      name: meta.name,
      size: meta.size ?? 0,
      date: meta.date ?? '',
      dataUrl: meta.dataUrl ?? '',
      kind: /\.docx$/i.test(meta.name) ? 'docx' : 'pdf',
      total: 0, pages: [], pct: 0, verifiedDate: null, verifiedAt: null, user: true,
    });
  });

  // per-document verification progress overrides
  keysWithPrefix('etc_comp_prog_').forEach(({ key, suffix: orgId }) => {
    const map = readJSON<Record<string, StoredProgress>>(key, {});
    (reg[orgId] || []).forEach((d) => {
      const p = map?.[d.id];
      if (!p) return;
      if (Array.isArray(p.pages)) d.pages = p.pages;
      if (p.total != null) d.total = p.total;
      if (typeof p.pct === 'number') d.pct = p.pct;
      if (Array.isArray(p.history)) d.history = p.history;
      d.verifiedAt = p.verifiedAt || null;
      d.verifiedDate = p.verifiedDate || null;
    });
  });
}

/* ---------------- Notification queue ---------------- */

/** Five demo notifications — one of each kind — for the presentation. */
export function seedNotifications(): Notification[] {
  const stored = readJSON<Notification[]>('etc_notifications', []);
  if (stored.length) return stored;
  const H = 1000 * 60 * 60;
  return [
    { id: 'seed-1', kind: 'warn',   title: 'TEDI tiene 3 datos pendientes de actualizar', body: 'Tecnología y Educación Innovadora E.I.R.L · Nombre comercial, Estaciones de trabajo y C.P. registrado', ts: Date.now() - 2 * H,  read: false },
    { id: 'seed-2', kind: 'info',   title: 'Solicitud de cambio de contacto legal', body: 'Naska Digital · pendiente de aprobación por Admin ETC', ts: Date.now() - 6 * H,  read: false },
    { id: 'seed-3', kind: 'ok',     title: 'Reporte Compliance verificado al 100%', body: 'SEMCO · “Código de conducta del socio”', ts: Date.now() - 26 * H, read: false },
    { id: 'seed-4', kind: 'err',    title: 'Importación de Excel con 2 filas rechazadas', body: 'EADICOL · revisa el archivo de instructores y vuelve a cargar', ts: Date.now() - 30 * H, read: true },
    { id: 'seed-5', kind: 'delete', title: 'Instructor desasociado de un site', body: 'Vera Quintana Asociados · 1 instructor quedó sin afiliación', ts: Date.now() - 49 * H, read: true },
  ];
}

/* ---------------- Legal contact change requests ---------------- */

/**
 * Two pending demo requests so the Administration inbox is not empty.
 * The prototype hard-coded organizations that no longer exist in the
 * dataset, so these are bound to real seeded orgs instead.
 */
export function seedLegalRequests(orgs: Organization[]): LegalRequest[] {
  const stored = readJSON<LegalRequest[]>('etc_legal_reqs', []);
  if (stored.length) return stored;
  const pick = (i: number) => orgs[i] ?? orgs[0];
  const a = pick(3);
  const b = pick(6);
  if (!a || !b) return [];
  return [
    {
      id: 'LR-seed-1', OrgId: a.OrgId, OrgName: a.OrgName,
      first: 'Adriana', last: 'Salinas', email: 'a.salinas@' + hostOf(a),
      phone: '+52 55 1234 5678', doc: 'acta_nombramiento.pdf',
      date: '2026-05-18', status: 'pending',
      requestedBy: `${a.LegalContactFirstName} ${a.LegalContactLastName}`.trim() || 'Representante legal',
    },
    {
      id: 'LR-seed-2', OrgId: b.OrgId, OrgName: b.OrgName,
      first: 'Carlos', last: 'Reyes', email: 'c.reyes@' + hostOf(b),
      phone: '+56 2 2345 6789', doc: 'poder_notarial.pdf',
      date: '2026-05-21', status: 'pending',
      requestedBy: `${b.LegalContactFirstName} ${b.LegalContactLastName}`.trim() || 'Representante legal',
    },
  ];
}

/** Best-effort mail host from an organization's website. */
function hostOf(o: Organization): string {
  const m = /^(?:https?:\/\/)?(?:www\.)?([^/]+)/i.exec(o.OrgWebAddress || '');
  return m?.[1] || 'partner.demo';
}

/* ---------------- Auth policy ---------------- */

/** Which sign-in methods each user type may use.
 *  'required' = always on (locked) · 'optional' = togglable · 'off' = not allowed */
export const AUTH_POLICY: Record<ManagedRole, AuthPolicy> = {
  ETC_ADMIN:   { password: 'required', twoFactor: 'required', microsoft: 'optional' },
  PARTNER_MGR: { password: 'required', twoFactor: 'optional', microsoft: 'optional' },
  SITE_MGR:    { password: 'required', twoFactor: 'optional', microsoft: 'optional' },
  SITE_ADMIN:  { password: 'required', twoFactor: 'optional', microsoft: 'optional' },
  INSTRUCTOR:  { password: 'required', twoFactor: 'off',      microsoft: 'optional' },
};

/* ---------------- Managed users ---------------- */

const deburr = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const U_STATUS = ['active', 'active', 'active', 'active', 'invited', 'disabled'] as const;
const BASE_DATE = new Date('2026-05-31T00:00:00');

export function buildAppUsers(orgs: Organization[], sites: Site[]): AppUser[] {
  const R = mulberry32(20240518);
  const pick = <T,>(arr: readonly T[]): T => arr[Math.floor(R() * arr.length)];
  const maybe = (p: number) => R() < p;
  const dateAgo = (d: number) => {
    const t = new Date(BASE_DATE);
    t.setDate(t.getDate() - d);
    return t.toISOString().slice(0, 10);
  };

  const users: AppUser[] = [];
  // USR-40001 is reserved for the ETC admin pushed below, so generated
  // accounts start at USR-40002
  let uCounter = 1;

  function makeUser(role: ManagedRole, orgId: string | null, siteId: string | null): AppUser {
    uCounter++;
    const fn = pick(FNAMES);
    const ln = pick(LNAMES);
    const status = pick(U_STATUS);
    const pol = AUTH_POLICY[role];
    const handle = (deburr(fn)[0] + deburr(ln)).toLowerCase().replace(/[^a-z]/g, '');
    return {
      id: 'USR-' + String(40000 + uCounter).padStart(4, '0'),
      FirstName: fn,
      LastName: ln,
      Email: `${handle}@${orgId ? 'partner.demo' : 'etcmco.com'}`,
      role,
      orgId,
      siteId,
      status,
      auth: {
        password: true,
        twoFactor: pol.twoFactor === 'required' ? true : pol.twoFactor === 'optional' ? maybe(0.45) : false,
        microsoft: pol.microsoft === 'required' ? true : pol.microsoft === 'optional' ? maybe(0.35) : false,
      },
      lastActive: status === 'invited' ? null : dateAgo(Math.floor(R() * 45)),
    };
  }

  // ETC staff
  users.push({
    id: 'USR-40001', FirstName: 'Admin', LastName: 'ETC', Email: 'admin@etc.io',
    role: 'ETC_ADMIN', orgId: null, siteId: null, status: 'active',
    auth: { password: true, twoFactor: true, microsoft: false }, lastActive: dateAgo(0),
  });
  // one partner manager per organization
  orgs.slice(0, 14).forEach((o) => users.push(makeUser('PARTNER_MGR', o.OrgId, null)));
  // site managers / admins across sites
  sites.slice(0, 26).forEach((s, i) =>
    users.push(makeUser(i % 2 === 0 ? 'SITE_MGR' : 'SITE_ADMIN', s.OrgId, s.SiteId)),
  );
  // NOTE: instructors have no portal access, so no accounts are created for
  // them. Their records still live in the Instructores section, managed by
  // ETC Admin, partner managers and site managers.
  return users;
}

/* ---------------- Demo sign-in identities ---------------- */

export const DEMO_USERS: DemoUser[] = [
  { username: 'admin@etc.io',      password: 'demo', role: 'ETC_ADMIN',   name: 'Admin ETC',        orgId: null },
  { username: 'gerente@tedi.demo', password: 'demo', role: 'PARTNER_MGR', name: 'Fredy Zorrilla',   orgId: 'ORG10203', orgIds: ['ORG10203'] },
  { username: 'sitemgr@tedi.demo', password: 'demo', role: 'SITE_MGR',    name: 'Lourdes Palomino', orgId: 'ORG10203', siteId: 'AM102223' },
];

/** A partner manager / partner admin may be tied to several organizations.
 *  Returns every org id in the user's scope (always >= 1 when set). */
export function userOrgIds(user: DemoUser | null | undefined): string[] {
  if (!user) return [];
  if (Array.isArray(user.orgIds) && user.orgIds.length) return user.orgIds;
  return user.orgId ? [user.orgId] : [];
}
